import PropTypes from "prop-types"
import React from "react"
import styled from "styled-components"
import Theme from "../../theme/theme"
import { Link } from "gatsby"
import LinkButton from "../common/link-button"
import GatsbyImage from "../common/gatsby-image"
import { getLanguage, getTranslation } from "../../utils"

const StyledRecipeBox = styled.li`
  display: flex;
  width: 50%;
  height: 26rem;
  padding-right: 3rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    min-height: 14rem;
    padding: 0;

    &:last-child {
      margin-bottom: 2rem;
    }
  }

  @media (min-width: 769px) {
    margin-bottom: 4rem;

    &:nth-child(2n) {
      padding-left: 3rem;
      padding-right: 0;
    }
  }

  ${props =>
    props.isProductPage &&
    `
      width: 100%;
      margin-bottom:0 !important;
      padding: 0 3rem;
  `};

  ${props =>
    props.$isAll &&
    `
      .box-data {
        color: #fff;
        background-color: ${props.theme.crockpot.recipeOdd} !important;
      }
  `};
`

const StyledInnerRecipeBox = styled.div`
  overflow: hidden;
  position: relative;
  display: flex;
  width: 100%;

  > div {
    &:first-child:not(.box-data) {
      width: 0;
    }
  }

  .gatsby-image-wrapper {
    position: absolute !important;
    width: 100%;
    z-index: 0;
    transition: 200ms linear;
  }

  &:hover {
    .gatsby-image-wrapper {
      transform: scale(1.05);
    }
  }
`

const StyledRecipeData = styled.div`
  position: relative;
  display: flex;
  max-width: 50%;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem 2rem;
  margin-left: auto;
  z-index: 1;

  .copy {
    display: none;
  }

  @media (max-width: 1024px) {
    justify-content: center;
    max-width: 66.666%;
    padding: 2rem;
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }

  h2 {
    color: ${props => props.theme.crockpot.textAlt};
    font-family: ${props => props.theme.crockpot.secondFont};
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 2.2rem;
    text-transform: uppercase;
    text-align: center;
    margin: 0;

    @media (min-width: 1024px) {
      font-size: 3rem;
      line-height: 3.5rem;
    }
  }

  p {
    color: ${props => props.theme.crockpot.textAlt};
    font-family: ${props => props.theme.crockpot.secondFont};
    font-size: 1.4rem;
    line-height: 1.8rem;
    text-transform: uppercase;
    margin-top: 1rem;

    @media (min-width: 1024px) {
      font-size: 1.9rem;
      line-height: 2.2rem;
    }
  }

  background: ${props => props.recipeCategory};

  a:last-child {
    position: relative;
    background-color: ${props => props.recipeCategory};
    margin-top: 2rem;
    z-index: 3;

    &:hover {
      color: ${props => props.recipeCategory};
      border-color: ${props => props.recipeCategory};
      background-color: #fff;
    }
  }

  ${props =>
    props.$isAll &&
    `
      max-width: 100%;
      justify-content: center;
      background:rgba(173,205,236,0.8);
      padding: 2rem;

      h2 {
        height: auto;
        margin-bottom: 1rem;

        @media (min-width: 1024px) {
         margin-bottom: 2rem;
        }
      }

      a {
        position: relative;
        bottom: 0;
      }
    `};
`

const RecipeLink = styled(props => <Link {...props} />)`
  position: absolute !important;
  top: 0;
  left: ${props => (props.$isAll ? "0%" : "-100%")};
  width: ${props => (props.$isAll ? "100%" : "200%")};
  height: 100%;
  z-index: 2;
`

const StyledLinkButton = styled(LinkButton)`
  @media (max-width: 769px) {
    border-width: 0.1rem;
  }
`

const SnowGatsbyImage = styled(GatsbyImage)`
  .gatsby-image-wrapper picture img {
    background: transparent !important;
  }
`

const RecipeBox = ({
  recipe = {},
  image = "",
  title = "",
  isAll = false,
  isProductPage = false,
}) => {
  if (!recipe.recipeName && !isAll) return null

  const recipeLink = `${getLanguage()}/${getTranslation("recipes")}`
  const fullRecipeLink = `${recipeLink}${
    recipe?.slug ? "/" + recipe?.slug : ""
  }`

  return (
    <StyledRecipeBox
      className={isAll ? "all-box" : null}
      $isAll={isAll}
      isProductPage={isProductPage}
      data-testid="recipe-box"
    >
      <StyledInnerRecipeBox>
        <GatsbyImage
          image={image ? image : recipe?.image?.gatsbyImageData}
          alt=""
        />
        {recipe?.category === "Christmas" && (
          <>
            <SnowGatsbyImage title="snow" />
            <a
              href="https://www.freepik.com/free-vector/realistic-falling-snowflakes-transparent-background_3505737.htm#query=snowfall%20transparent&position=15&from_view=keyword"
              className="copy"
            >
              Freepik
            </a>
          </>
        )}
        <StyledRecipeData
          className="box-data"
          $isAll={isAll}
          recipeCategory={Theme.crockpot.recipeCategories[recipe?.category]}
        >
          <h2>{title ? title : recipe?.recipeName}</h2>
          {recipe?.category && (
            <p>{getTranslation(`${recipe?.category.toLowerCase()}-cookers`)}</p>
          )}
          <RecipeLink to={fullRecipeLink} $isAll={isAll} />
          <StyledLinkButton
            title={`
              ${
                isAll
                  ? getTranslation("all-recipes")
                  : getTranslation("get-the-recipe")
              }`}
            link={fullRecipeLink}
          />
        </StyledRecipeData>
      </StyledInnerRecipeBox>
    </StyledRecipeBox>
  )
}

RecipeBox.propTypes = {
  recipe: PropTypes.object,
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  title: PropTypes.string,
  isAll: PropTypes.bool,
  isProductPage: PropTypes.bool,
}
export default RecipeBox
